import * as React from "react";
import { useCombobox } from "downshift";
import { clsx } from "clsx";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
    IAutocompleteResponse,
    IAutocompleteResponseItem,
} from "../interfaces/search";
import { fetchAutocomplete } from "../api/searchapi";
import { IInitOptions } from "../interfaces/initoptions";
import { getInitOptions } from "../api/initoptions";
import IconSearch from "./icon/iconsearch";
import SuggestIcon from "./suggesticon";

export const useAutocomplete = (
    jurisdictionId: string,
    search: string,
): UseQueryResult<IAutocompleteResponse, Error> => {
    return useQuery({
        queryKey: ["autocomplete", jurisdictionId, search],
        queryFn: () => fetchAutocomplete(jurisdictionId, search),
    });
};

const initOptions: IInitOptions = getInitOptions();

const SearchBox = () => {
    const [searchValue, setSearchValue] = React.useState(
        initOptions.query || "",
    );
    const [searchError, setSearchError] = React.useState("");

    const { data, isLoading, error } = useAutocomplete(
        initOptions.jurisdictionId.toString(),
        searchValue,
    );

    const items: Array<IAutocompleteResponseItem> = data?.items || [];

    const {
        isOpen,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getItemProps,
        highlightedIndex,
        selectItem,
        selectedItem,
        inputValue,
    } = useCombobox({
        items,
        inputValue: searchValue,
        onInputValueChange: ({ inputValue: newValue }) => {
            setSearchValue(newValue || "");
            setSearchError("");
        },
        itemToString(item: IAutocompleteResponseItem | null) {
            return item ? item.text : "";
        },
    });

    const handleSearch = (searchText: string) => {
        const searchTrim = searchText.trim();
        if (searchTrim) {
            window.location.href = `${initOptions.resultUrl}?q=${
                encodeURIComponent(searchTrim)
            }`;
        } else {
            setSearchError("Please enter some search criteria");
            console.log("Empty");
        }
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        downshift: { inputValue: string }
    ) => {
        if (event.key === "Enter") {
            handleSearch(downshift.inputValue);
        }
    };

    const classSearchBox = searchError
        ? "is-invalid facility-search-box"
        : "facility-search-box";

    // 1. Separate variable for the list items (<li> elements)
    const listItems =
        isOpen && items.length > 0 ? (
            items.map((item: IAutocompleteResponseItem, index) => (
                <li
                    className={clsx(
                        highlightedIndex === index && "bg-primary-subtle",
                        // Note: selectedItem check removed as it wasn't used in the old code, 
                        // but you can add it back if needed: selectedItem === item && "fw-bold",
                    )}
                    key={item.id}
                    {...getItemProps({
                        item,
                        index,
                        "data-testid": `downshift-item-${index}`,
                    })}
                >
                    <SuggestIcon icon={item.icon} />
                    <span className="ms-2">{item.text}</span>
                </li>
            ))
        ) : null;

    // 2. Determine the classes for the menu container (<ul>)
    const menuClasses = clsx(
        "facility-search-suggest",
        // Apply "open" class only when the menu is logically open AND has content
        isOpen && items.length > 0 && "open"
    );

    return (
        <div className="facility-search">
            <div className={classSearchBox}>
                <div className="facility-search-button">
                    <button onClick={() => handleSearch(inputValue)}>
                        <IconSearch />
                    </button>
                </div>
                <input
                    type="search"
                    className="facility-search-input"
                    {...getInputProps({
                        placeholder: "Search by County, City, Zip or Name",
                        onKeyDown: (event) =>
                            handleKeyDown(event, { inputValue }),
                    })}
                />
            </div>

            {/* 
              FIX: The UL must always be rendered to receive getMenuProps()
              We only conditionally render the list items (<li>) inside it.
            */}
            <ul {...getMenuProps()} className={menuClasses}>
                {listItems}
            </ul>

            {searchError && (
                <div className="facility-search-error">{searchError}</div>
            )}
        </div>
    );
};

export default SearchBox;
